import React, { useState } from 'react'
import * as XLSX from 'xlsx';

function NewSpec({ setSpecifications, specifications }) {
  const [headingInput, setHeadingInput] = useState('')
  const [rowDrafts, setRowDrafts] = useState({})
  const [editingRows, setEditingRows] = useState({})

  const rowKey = (heading, index) => `${heading}::${index}`

  const readExcel = (file) => {
    const promise = new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(file)

      fileReader.onload = (e) => {
        const bufferArray = e.target.result
        const wb = XLSX.read(bufferArray, { type: 'buffer' })
        const wsname = wb.SheetNames[0]
        const ws = wb.Sheets[wsname]
        const data = XLSX.utils.sheet_to_json(ws)
        const specs = []

        for (let item of data) {
          let heading = item.Heading.trim()
          let title = item.Title.trim()
          let description = String(item.Description).trim()
          let spec = specs.find((s) => s.heading === heading)
          if (!spec) {
            spec = { heading, specification: [] }
            specs.push(spec)
          }
          spec.specification.push({ title, description })
        }
        resolve(specs)
      }

      fileReader.onerror = (error) => {
        reject(error)
      }
    })

    promise.then((d) => {
      setSpecifications((prevSpecs) => {
        const specs = Object.values({ ...(prevSpecs || []), ...d })
        return specs
      })
    })
  }

  const startSection = () => {
    const heading = headingInput.trim()
    if (!heading) return

    setSpecifications((prevSpecs) => {
      const nextSpecs = [...(prevSpecs || [])]
      if (!nextSpecs.find((s) => s.heading === heading)) {
        nextSpecs.push({ heading, specification: [] })
      }
      return nextSpecs
    })

    setHeadingInput('')
    setRowDrafts({ ...rowDrafts, [heading]: { title: '', description: '' } })
  }

  const addRow = (heading) => {
    const title = rowDrafts[heading]?.title?.trim() || ''
    const description = rowDrafts[heading]?.description?.trim() || ''
    if (!title || !description) return

    setSpecifications((prevSpecs) => {
      const nextSpecs = (prevSpecs || []).map((section) => {
        if (section.heading !== heading) return section
        return {
          ...section,
          specification: [...section.specification, { title, description }]
        }
      })
      return nextSpecs
    })

    setRowDrafts({
      ...rowDrafts,
      [heading]: { title: '', description: '' },
    })
  }

  const startEditRow = (heading, index, currentRow) => {
    const key = rowKey(heading, index)
    setEditingRows({
      ...editingRows,
      [key]: { title: currentRow.title, description: currentRow.description },
    })
  }

  const cancelEditRow = (heading, index) => {
    const key = rowKey(heading, index)
    const nextEditing = { ...editingRows }
    delete nextEditing[key]
    setEditingRows(nextEditing)
  }

  const saveRow = (heading, index) => {
    const key = rowKey(heading, index)
    const draft = editingRows[key]
    if (!draft?.title?.trim() || !draft?.description?.trim()) return

    setSpecifications((prevSpecs) => {
      const nextSpecs = (prevSpecs || []).map((section) => {
        if (section.heading !== heading) return section
        const updatedRows = (section.specification || []).map((row, idx) => {
          if (idx !== index) return row
          return { title: draft.title.trim(), description: draft.description.trim() }
        })
        return { ...section, specification: updatedRows }
      })
      return nextSpecs
    })

    const nextEditing = { ...editingRows }
    delete nextEditing[key]
    setEditingRows(nextEditing)
  }

  const deleteRow = (heading, index) => {
    setSpecifications((prevSpecs) => {
      const nextSpecs = (prevSpecs || []).map((section) => {
        if (section.heading !== heading) return section
        const updatedRows = (section.specification || []).filter((_, idx) => idx !== index)
        return { ...section, specification: updatedRows }
      })
      return nextSpecs
    })

    const key = rowKey(heading, index)
    const nextEditing = { ...editingRows }
    delete nextEditing[key]
    setEditingRows(nextEditing)
  }

  const deleteSection = (heading) => {
    setSpecifications((prevSpecs) => {
      const nextSpecs = (prevSpecs || []).filter((section) => section.heading !== heading)
      return nextSpecs
    })

    const nextEditing = { ...editingRows }
    Object.keys(nextEditing).forEach((key) => {
      if (key.startsWith(`${heading}::`)) {
        delete nextEditing[key]
      }
    })
    const nextDrafts = { ...rowDrafts }
    delete nextDrafts[heading]
    setEditingRows(nextEditing)
    setRowDrafts(nextDrafts)
  }

  return (
    <>
      {specifications && specifications.length ? (
        ''
      ) : (
        <div className='mb-3'>
          <span className='info_txt mt-0'>No Specifications added. Upload a XLSX file or add manually.</span>
        </div>
      )}

      <input
        type='file'
        className='custom-file-input'
        onChange={(e) => {
          const file = e.target.files[0]
          readExcel(file)
        }}
      />

      <div className='card mt-3'>
        <div className='card-header'>
          <label className='form-label mb-0'>Add specifications manually</label>
        </div>
        <div className='card-body'>
          <div className='row g-2 align-items-end'>
            <div className='col-md-8'>
              <label className='form-label'>Section heading</label>
              <input
                type='text'
                className='form-control'
                placeholder='e.g., General Details'
                value={headingInput}
                onChange={(e) => setHeadingInput(e.target.value)}
              />
            </div>
            <div className='col-md-4 d-grid'>
              <button type='button' className='btn btn-success mt-md-4' onClick={startSection}>
                Add Specification Section
              </button>
            </div>
          </div>
        </div>
      </div>

      {specifications?.map((item, i) => {
        const draft = rowDrafts[item.heading] || { title: '', description: '' }
        return (
          <div key={i} className='card mt-3'>
            <div className='card-header d-flex justify-content-between align-items-center'>
              <h5 className='mb-0'>{item.heading}</h5>
              <button
                type='button'
                className='btn btn-sm btn-outline-danger'
                onClick={() => deleteSection(item.heading)}
              >
                Delete Section
              </button>
            </div>
            <div className='card-body'>
              <div className='row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3'>
                {item.specification?.map((specRow, idx) => (
                  <div key={idx} className='col'>
                    <div className='p-3 border rounded h-100 d-flex flex-column justify-content-between gap-2'>
                      {editingRows[rowKey(item.heading, idx)] ? (
                        <>
                          <div className='mb-2'>
                            <label className='form-label'>Title</label>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={editingRows[rowKey(item.heading, idx)]?.title || ''}
                              onChange={(e) =>
                                setEditingRows({
                                  ...editingRows,
                                  [rowKey(item.heading, idx)]: {
                                    ...editingRows[rowKey(item.heading, idx)],
                                    title: e.target.value,
                                  },
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className='form-label'>Description</label>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={editingRows[rowKey(item.heading, idx)]?.description || ''}
                              onChange={(e) =>
                                setEditingRows({
                                  ...editingRows,
                                  [rowKey(item.heading, idx)]: {
                                    ...editingRows[rowKey(item.heading, idx)],
                                    description: e.target.value,
                                  },
                                })
                              }
                            />
                          </div>
                          <div className='d-flex gap-2 mt-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-success'
                              onClick={() => saveRow(item.heading, idx)}
                            >
                              Save
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-secondary'
                              onClick={() => cancelEditRow(item.heading, idx)}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <div className='fw-semibold'>{specRow.title}</div>
                            <div className='text-muted small'>{specRow.description}</div>
                          </div>
                          <div className='d-flex gap-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-primary'
                              onClick={() => startEditRow(item.heading, idx, specRow)}
                            >
                              Edit
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => deleteRow(item.heading, idx)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className='row g-2 align-items-end mt-3'>
                <div className='col-md-4'>
                  <label className='form-label'>Title</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='e.g., Model Name'
                    value={draft.title}
                    onChange={(e) =>
                      setRowDrafts({
                        ...rowDrafts,
                        [item.heading]: { ...draft, title: e.target.value },
                      })
                    }
                  />
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>Description</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='e.g., MOTO G96'
                    value={draft.description}
                    onChange={(e) =>
                      setRowDrafts({
                        ...rowDrafts,
                        [item.heading]: { ...draft, description: e.target.value },
                      })
                    }
                  />
                </div>
                <div className='col-md-2 d-grid'>
                  <button
                    type='button'
                    className='btn btn-outline-success'
                    onClick={() => addRow(item.heading)}
                  >
                    Add Row
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}

export default NewSpec
